import {
  acceptMemberInviteRequestSchema,
  changePasswordRequestSchema,
  EMPLOYEE_APP_NAME,
  PARTNER_SIGN_IN_CLIENT,
} from '@instanexus/contracts';
import { errorMessage, useTermsAgreement, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  ProviderButton,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  TermsAgreementText,
} from '@instanexus/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { useApiClient } from '@/hooks/use-api-client';
import { centralLoginPath, INVITE_EMAIL_PARAM, INVITE_TOKEN_PARAM } from '@/lib/auth-paths';
import { startProviderInvite } from '@/lib/federated-start';
import { parseSignInRequest } from '@/lib/sign-in-request';

/** Same fallback reasoning as `login-page.tsx` — never a declared `ErrorCode`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

/** Full-page navigation; a function so the unit test can observe it. */
function redirectTo(url: string): void {
  window.location.assign(url);
}

/** The one field that exists on this form and not in the contract. */
const CONFIRM_FIELD = 'confirmNewPassword';

/**
 * The password half of the contract, plus the confirmation box.
 *
 * `email` and `token` are deliberately NOT form fields: neither is something the invitee may
 * edit, and a `token` in a registered input would put a capability into a DOM attribute. They
 * come off the query string, are validated once below with the CONTRACT's own rules, and go
 * straight into the request body.
 *
 * The confirmation borrows `changePasswordRequestSchema.shape.currentPassword` (a plain
 * non-empty, bounded string) rather than `passwordSchema`, exactly as the central reset-password page
 * does: re-running the POLICY here would report "too short" against the confirmation box when
 * the real problem is a typo.
 */
const acceptInviteFormSchema = acceptMemberInviteRequestSchema
  .pick({ newPassword: true })
  .extend({ [CONFIRM_FIELD]: changePasswordRequestSchema.shape.currentPassword })
  .superRefine((values, ctx) => {
    if (values.newPassword === values.confirmNewPassword) return;
    ctx.addIssue({
      code: 'custom',
      path: [CONFIRM_FIELD],
      // Replaced by translated copy in `localizeErrors` before anything renders — zod's own
      // prose is untranslated English and is never shown (docs/011).
      message: 'passwords must match',
    });
  });

interface AcceptInviteFormValues {
  newPassword: string;
  confirmNewPassword: string;
}

function isBranch(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Same technique as the central reset-password page: none of zod's own English prose is rendered. */
function localizeErrors(errors: unknown, validationMessage: string, mismatchMessage: string): void {
  if (!isBranch(errors)) return;
  for (const [name, error] of Object.entries(errors)) {
    if (!isBranch(error)) continue;
    error['message'] =
      name === CONFIRM_FIELD && error['type'] === 'custom' ? mismatchMessage : validationMessage;
  }
}

/**
 * The last step of a member invite, for every app: the invitee chooses a password and their
 * account becomes usable. `?client=` (admin | visitor | directory | partner) only changes the copy and
 * where "done" leads — the form is identical; a partner invitation is accepted through the
 * partner route, adr/0035.
 *
 * PUBLIC — registered outside `RequireAuth`, because an invitee has no credential yet by
 * definition. The `?email=`/`?token=` pair is what the invitation email carries
 * (`memberInviteAcceptUrl`, API `identity/service.ts`); the token is single-use,
 * time-bounded and attempt-capped server-side, and this page never writes it anywhere but the
 * request body — no `localStorage`, no query key, no `data-*` attribute.
 *
 * Its states: missing-link (the error state, reachable by simply opening `/accept-invite`),
 * idle, submitting (`formState.isSubmitting`, so a double-click cannot burn the token twice),
 * refused — one translated message for the error CODE, `VERIFICATION_CODE_INVALID` covering
 * every cause on purpose (S26-TASK-018) — and done, which offers the way on to sign in but does
 * NOT navigate on its own: the invitee has just chosen a password and needs a beat to register
 * that it worked.
 */
export function AcceptInvitePage(): ReactElement {
  const { t } = useTranslation('auth');
  const agreement = useTermsAgreement();
  const api = useApiClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { client } = parseSignInRequest(searchParams);

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  const validationMessage = errorMessage(t, 'VALIDATION_FAILED');
  const mismatchMessage = t('acceptInvite.mismatch');

  const resolver = useMemo<Resolver<AcceptInviteFormValues>>(() => {
    const base = zodResolver(acceptInviteFormSchema);
    return async (values, context, options) => {
      const result = await base(values, context, options);
      localizeErrors(result.errors, validationMessage, mismatchMessage);
      return result;
    };
  }, [validationMessage, mismatchMessage]);

  const form = useForm<AcceptInviteFormValues>({
    resolver,
    defaultValues: { newPassword: '', confirmNewPassword: '' },
  });

  /**
   * Both halves of the link, validated with the contract's own rules rather than merely checked
   * for presence. A malformed address or an over-long token could never be redeemed, and
   * submitting it would leave the invitee looking at a form that silently does nothing — so it
   * is the same incomplete-link state as no query string at all.
   */
  const link = acceptMemberInviteRequestSchema.pick({ email: true, token: true }).safeParse({
    email: searchParams.get(INVITE_EMAIL_PARAM) ?? '',
    token: searchParams.get(INVITE_TOKEN_PARAM) ?? '',
  });

  const onSubmit = form.handleSubmit(async (values: AcceptInviteFormValues): Promise<void> => {
    if (!link.success) return;
    setErrorCode(null);
    try {
      // The confirmation box never goes on the wire — no endpoint reads it.
      const body = {
        email: link.data.email,
        token: link.data.token,
        newPassword: values.newPassword,
      };
      if (client === PARTNER_SIGN_IN_CLIENT) {
        // adr/0035: partner staff are not tenant members; their invite has its own route and limiter.
        await api.partnerAuth.acceptInvite(body);
      } else {
        await api.auth.acceptMemberInvite(body);
      }
      if (client === 'directory') {
        await navigate(centralLoginPath('directory', link.data.email));
        return;
      }
      setIsDone(true);
    } catch (error: unknown) {
      setErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
    }
  });

  const isSubmitting = form.formState.isSubmitting;

  // A failed or empty providers answer just means no button; the password form is never blocked.
  const providers = useQuery({
    queryKey: ['federation', 'providers'],
    queryFn: () => api.federation.providers(),
    staleTime: Infinity,
    retry: false,
  });
  const providerClient = client === 'admin' || client === 'directory' ? client : null;
  // Advisory too: a failed discovery just means no company button. Only the sign-in clients
  // that offer providers ask, and only for a valid link.
  const discovery = useQuery({
    queryKey: ['sso', 'discover', link.success ? link.data.email : ''],
    queryFn: () => api.sso.discover({ email: link.success ? link.data.email : '' }),
    enabled: providerClient !== null && link.success,
    retry: false,
    staleTime: Infinity,
  });
  const answer = discovery.data;
  const offersProvider = (provider: 'google' | 'microsoft'): boolean =>
    providerClient !== null &&
    link.success &&
    ((providers.data?.providers.includes(provider) ?? false) ||
      (answer?.method === 'company' && answer.provider === provider));
  const offersGoogle = offersProvider('google');
  const offersMicrosoft = offersProvider('microsoft');
  const offersEnterprise =
    answer?.method === 'company' &&
    answer.provider === 'enterprise' &&
    providerClient !== null &&
    link.success;
  const enterpriseName = answer?.method === 'company' ? answer.label : undefined;
  const enterpriseLabel =
    enterpriseName === undefined
      ? t('federated.acceptWithEnterpriseGeneric')
      : t('federated.acceptWithEnterprise', { name: enterpriseName });
  const [startingProvider, setStartingProvider] = useState<
    'google' | 'microsoft' | 'enterprise' | null
  >(null);

  const acceptWithProvider = async (
    provider: 'google' | 'microsoft' | 'enterprise',
  ): Promise<void> => {
    if (!link.success || providerClient === null) return;
    setErrorCode(null);
    setStartingProvider(provider);
    try {
      await startProviderInvite(
        api,
        provider,
        { client: providerClient, email: link.data.email, token: link.data.token },
        redirectTo,
      );
    } catch (error: unknown) {
      setErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
      setStartingProvider(null);
    }
  };

  const renderCard = (): ReactElement => {
    if (!link.success) {
      return (
        <>
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('acceptInvite.title')}
            </h1>
          </CardHeader>
          <CardContent>
            {/* `warning`, not `danger`: nothing failed and no request was made — the link
                itself is incomplete. `role="alert"` all the same, because this is the whole
                reason the person is looking at the page. (A `danger` Alert here would also be
                the hand-rolled write-failure banner `no-restricted-syntax` forbids; a refused
                SUBMIT is rendered below, through the error code.) */}
            <Alert variant="warning" role="alert">
              <AlertDescription>{t('acceptInvite.missingLink')}</AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter>
            <Button asChild variant="link" className="w-full">
              <Link to={centralLoginPath(client)}>{t('acceptInvite.signIn')}</Link>
            </Button>
          </CardFooter>
        </>
      );
    }

    if (isDone && client === 'visitor') {
      return (
        <>
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('acceptInvite.employee.doneTitle')}
            </h1>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <p className="text-sm text-primary text-pretty">
              {t('acceptInvite.employee.doneBody', {
                app: EMPLOYEE_APP_NAME,
                email: link.data.email,
              })}
            </p>
            <p className="text-sm text-muted text-pretty">{t('acceptInvite.employee.doneHint')}</p>
          </CardContent>
        </>
      );
    }

    if (isDone) {
      return (
        <>
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('acceptInvite.title')}
            </h1>
          </CardHeader>
          <CardContent>
            <Alert variant="success">
              <AlertTitle>{t('acceptInvite.success')}</AlertTitle>
            </Alert>
          </CardContent>
          <CardFooter>
            <Button asChild className="w-full">
              <Link to={centralLoginPath(client, link.data.email)}>{t('acceptInvite.signIn')}</Link>
            </Button>
          </CardFooter>
        </>
      );
    }

    return (
      <>
        <CardHeader>
          <h1 className="text-3xl font-semibold text-primary text-balance">
            {t('acceptInvite.title')}
          </h1>
          <CardDescription>
            {client === 'visitor'
              ? t('acceptInvite.employee.description', { app: EMPLOYEE_APP_NAME })
              : t('acceptInvite.description')}
          </CardDescription>
        </CardHeader>

        <Form {...form}>
          <form
            noValidate
            onSubmit={(event) => {
              void onSubmit(event);
            }}
          >
            <CardContent className="flex flex-col gap-4">
              {errorCode === null ? null : (
                <p role="alert" className="text-danger text-sm text-pretty">
                  {errorMessage(t, errorCode)}
                </p>
              )}

              {/* Read-only and not an input: the address is who the invite was issued to, and
                  the token only ever works against it, so there is nothing here to edit. */}
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-primary">{t('acceptInvite.emailLabel')}</p>
                <p className="text-sm text-muted break-words">{link.data.email}</p>
              </div>

              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('acceptInvite.passwordLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        aria-required
                        width="full"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('acceptInvite.passwordHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmNewPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('acceptInvite.confirmLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        aria-required
                        width="full"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>

            <CardFooter className="flex flex-col items-stretch gap-3">
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? t('acceptInvite.submitting') : t('acceptInvite.submit')}
              </Button>
              {offersEnterprise || offersGoogle || offersMicrosoft ? (
                <p className="text-center text-sm text-muted-foreground">{t('federated.or')}</p>
              ) : null}
              {offersEnterprise ? (
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  disabled={startingProvider !== null || isSubmitting}
                  aria-busy={startingProvider === 'enterprise'}
                  onClick={() => {
                    void acceptWithProvider('enterprise');
                  }}
                >
                  {startingProvider === 'enterprise'
                    ? t('federated.redirectingEnterprise')
                    : enterpriseLabel}
                </Button>
              ) : null}
              {offersGoogle ? (
                <ProviderButton
                  provider="google"
                  disabled={startingProvider !== null || isSubmitting}
                  aria-busy={startingProvider === 'google'}
                  onClick={() => {
                    void acceptWithProvider('google');
                  }}
                >
                  {startingProvider === 'google'
                    ? t('federated.redirecting')
                    : t('federated.acceptWithGoogle')}
                </ProviderButton>
              ) : null}
              {offersMicrosoft ? (
                <ProviderButton
                  provider="microsoft"
                  disabled={startingProvider !== null || isSubmitting}
                  aria-busy={startingProvider === 'microsoft'}
                  onClick={() => {
                    void acceptWithProvider('microsoft');
                  }}
                >
                  {startingProvider === 'microsoft'
                    ? t('federated.redirectingMicrosoft')
                    : t('federated.acceptWithMicrosoft')}
                </ProviderButton>
              ) : null}
              <p className="text-subtle text-center text-xs text-pretty">
                <TermsAgreementText
                  message={agreement.continueMessage}
                  termsUrl={agreement.termsUrl}
                  privacyUrl={agreement.privacyUrl}
                />
              </p>
            </CardFooter>
          </form>
        </Form>
      </>
    );
  };

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">{renderCard()}</Card>
    </AuthCardLayout>
  );
}
